// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CompanyService } from '@lfx/core/services';
import { AvatarComponent } from '@lfx/shared/components/avatar/avatar.component';
import { ButtonComponent } from '@lfx/shared/components/button';
import { HealthScoreVariant } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { EllipsisPipe } from '@lfx/shared/pipes/ellipsis.pipe';
import { TimeRangeSelectorComponent } from '@lfx/key-projects/components/time-range-selector/time-range-selector.component';
import { TimeRange, TimeRangeValue } from '@lfx/key-projects/entities';

@Component({
  selector: 'lfx-key-project-detail-header',
  imports: [
    CommonModule,
    AvatarComponent,
    ButtonComponent,
    EllipsisPipe,
    TimeRangeSelectorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="custom-layout-section-container">
      <div class="key-project-detail-header">
        <!-- Breadcrumb Navigation -->
        <div class="breadcrumb-container">
          <ng-container
            *ngFor="
              let elem of breadcrumb();
              let last = last;
              trackBy: trackByLabel
            "
          >
            <span
              [class]="last ? 'gray-breadcrumb' : 'blue-breadcrumb lf-link'"
              (click)="navigate(elem)"
            >
              {{ elem.label }}
            </span>
            <i
              *ngIf="!last"
              class="fal fa-chevron-right breadcrumb-separator"
            ></i>
          </ng-container>
        </div>
        <!-- Project information -->
        <div class="project-info">
          <!-- Left side: Project avatar, name and description -->
          <div class="project-main-info">
            <lfx-avatar
              [imageURL]="logoUrl() || ''"
              [name]="projectName() || slug()"
              type="project"
              size="lg"
              [isObjectCover]="false">
            </lfx-avatar>
            <div class="project-text-info">
              <h1 class="project-title lf-heading-xl">
                {{ projectName() || slug() }}
              </h1>
              <p class="project-description lf-body-sm">
                {{ description() | ellipsis: 158 }}
                <span> Source:
                  <a
                    [href]="insightsUrl()"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="lf-link"
                  >LFX Insights <img
                      src="assets/img/svgs/arrow-up-right-from-square.svg"
                      alt="Opens in new window"
                      class="external-link-icon"
                    /></a></span>
              </p>
            </div>
          </div>

          <!-- Right side: Stats -->
          <div class="project-stats">
            <div class="stat-item">
              <div class="stat-label lf-body-bold-sm">First commit</div>
              <div class="stat-value lf-body-base">{{ firstCommit() }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label lf-body-bold-sm">Software value</div>
              <div class="stat-value lf-body-base">{{ softwareValue() }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label lf-body-bold-sm">Health score</div>
              <div class="stat-value">
                <div
                  *ngIf="healthScoreVariant()"
                  [class]="
                    'health-badge lf-body-bold-sm ' + healthBadgeClasses()
                  "
                  [attr.aria-label]="healthAriaLabel()"
                >
                  {{ healthDisplayText() }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Menu and Section Footer -->
        <div class="navigation-section">
          <!-- Navigation Menu with Pill Buttons -->
          <div class="navigation-menu">
            <lfx-button
              variant="menu"
              size="large"
              svgIcon="assets/img/svgs/chart-scatter.svg"
              iconPosition="left"
              [isActive]="activeTab() === 'our-influence'"
              (clicked)="setActiveTab('our-influence')"
            >
              Our influence
            </lfx-button>
            <lfx-button
              variant="menu"
              size="large"
              svgIcon="assets/img/svgs/list-ol.svg"
              iconPosition="left"
              [isActive]="activeTab() === 'leaderboards'"
              (clicked)="setActiveTab('leaderboards')"
            >
              Leaderboards
            </lfx-button>
          </div>


          <lfx-time-range-selector
            [selectedRange]="selectedRange()"
            [disabled]="false"
            (rangeSelected)="onRangeChange($event)"
          >
          </lfx-time-range-selector>
        </div>
      </div>
    </section>
  `,
  styleUrls: ['./key-project-detail-header.component.scss'],
})
export class KeyProjectDetailHeaderComponent {
  private readonly router = inject(Router);
  private readonly companyService = inject(CompanyService);

  // Route parameters
  slug = input.required<string>();
  projectName = input<string>();

  // Project data inputs
  logoUrl = input<string>();
  description = input<string>();
  healthScoreVariant = input<HealthScoreVariant>();
  firstCommit = input<string>();
  softwareValue = input<string>();

  // Navigation inputs and outputs
  activeTab = input<string>('our-influence');
  tabChanged = output<string>();

  // Time range inputs and outputs
  selectedRange = input<TimeRangeValue>(TimeRange.DEFAULT);
  rangeChanged = output<TimeRangeValue>();

  // Breadcrumb data structure following established pattern
  breadcrumb = computed(() => [
    {
      label: 'Key Projects',
      url: `/${this.companyService.getCurrentCompanySlug()}/key-projects`,
    },
    {
      label: this.projectName() || this.slug(), // Use project name if available, otherwise slug
      url: null, // null means current/last item
    },
  ]);

  // Health score badge computed properties
  protected readonly healthBadgeClasses = computed(() => {
    const variant = this.healthScoreVariant();
    return variant ? `health-badge--${variant}` : '';
  });

  protected readonly healthDisplayText = computed(() => {
    const variant = this.healthScoreVariant();
    return variant ? KeyProjectsHelper.getHealthScoreDisplayText(variant) : '';
  });

  protected readonly healthAriaLabel = computed(() => {
    const text = this.healthDisplayText();
    return `Health score: ${text}`;
  });

  // LFX Insights URL computation following the same pattern as health-score-popover-content
  protected readonly insightsUrl = computed(() => {
    const slug = this.slug();
    return slug ? `https://insights.linuxfoundation.org/project/${slug}` : '#';
  });

  protected navigate(elem: { label: string; url: string | null }): void {
    if (elem.url) {
      this.router.navigateByUrl(elem.url);
    }
  }

  protected trackByLabel(
    index: number,
    item: { label: string; url: string | null }
  ): string {
    return item.label;
  }

  protected navigateToKeyProjects(): void {
    const currentCompanySlug = this.companyService.getCurrentCompanySlug();
    if (currentCompanySlug) {
      this.router.navigate([currentCompanySlug, 'key-projects']);
    }
  }

  protected setActiveTab(tab: string): void {
    this.tabChanged.emit(tab);
  }

  protected onRangeChange(range: TimeRangeValue): void {
    this.rangeChanged.emit(range);
  }
}
